/**
 * Notices when a newer build has been deployed next to the running one (the play-test tunnel serves `dist` straight
 * from disk, so a rebuild is live the moment it lands). The build id is the one `precache.json` carries (see
 * vite.config.ts, td:offline-cache). Checks on a timer while the page is visible and whenever it comes back to the
 * foreground; offline or a missing file simply means "no news", and the next check tries again.
 *
 * The running build is the id the build stamped into the page's own index.html (`<meta name="highmarch-build">`), not the
 * first `precache.json` read: the service worker can boot the page from a cached, older index.html while the network
 * already has the new manifest, and taking that as the baseline would keep a phone on the old build for the whole
 * session. Only a page without the stamp (a build from before it) falls back to the first manifest it can read.
 */
export class BuildWatcher {
  static readonly IntervalMs = 15_000;
  /** The meta tag vite.config.ts (td:offline-cache) writes into dist/index.html. */
  static readonly MetaName = "highmarch-build";
  private current: string | null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly onNewBuild: (build: string) => void, private readonly url = "./precache.json", running: () => string | null = runningBuild) {
    this.current = running();
  }

  async start(): Promise<void> {
    if (this.timer !== null) return;
    this.timer = setInterval(() => { if (document.visibilityState === "visible") void this.check(); }, BuildWatcher.IntervalMs);
    document.addEventListener("visibilitychange", this.onVisible);
    await this.check();
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    document.removeEventListener("visibilitychange", this.onVisible);
  }

  private readonly onVisible = (): void => { if (document.visibilityState === "visible") void this.check(); };

  /** Public for tests: one poll. */
  async check(): Promise<void> {
    const latest = await this.fetchBuild();
    if (latest === null) return;
    if (this.current === null) { this.current = latest; return; }   // unstamped page: the first read is the baseline
    if (latest === this.current) return;
    this.current = latest;
    this.onNewBuild(latest);
  }

  private async fetchBuild(): Promise<string | null> {
    try {
      const res = await fetch(`${this.url}?t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return null;
      const doc = (await res.json()) as { build?: unknown };
      return typeof doc.build === "string" ? doc.build : null;
    } catch {
      return null;
    }
  }
}

/** The build id stamped into the running page, or null (dev server, or a page built before the stamp). */
export function runningBuild(): string | null {
  const v = typeof document === "object" ? document.querySelector<HTMLMetaElement>(`meta[name="${BuildWatcher.MetaName}"]`)?.content : undefined;
  return v ? v : null;
}
